import React, { useContext, useState, useMemo } from "react";
import { Modal, DatePicker, Upload, Radio, Space } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";

// API
import { importCSVFile } from "api/pricerApi";
// Helpers
import { toast } from "helpers/generalHelper";
import { toastImportValidationResponse } from "helpers/importCellErrors";
import { isErrorResponse } from "helpers/tradeCaptureHelper";
import { pricerWorkingDate } from "helpers/utcCalendarDate";
import {
  ACTIONS,
  CATEGORIES,
  COMPONENT_NAMES,
  CONTEXTS,
  EVENT_NAMES,
  sendEvent,
} from "helpers/googleAnalyticsHelper";
import { GlobalContext } from "contexts/GlobalContext";
import { PricerContext } from "contexts/PricerContext";
import { CancelButton, PrimaryButton } from "components/Common/CustomButtons/CustomButtons";
import VolCalcFailedModal from "components/Common/VolCalcFailedModal/VolCalcFailedModal";
import useDraggableModal from "hooks/useDraggableModal";
import "./CSVImportModal.scss";

const PRICER_DESTINATION = "pricer";
const AUDIT_TRAIL_DESTINATION = "audit_trail";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(utc);
dayjs.extend(timezone);

function calendarDayjs(value) {
  const day = pricerWorkingDate(value);
  return dayjs.utc(day).utcOffset(dayjs().utcOffset(), true);
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const MAX_ROWS = 50;

// NOTE: These limits are for UX only. Backend MUST enforce these limits server-side for security.
// Client-side validation can be bypassed, so backend validation is critical.

const CSVImportModal = ({ isOpen, onClose, refetchOptionRow }) => {
  const [fileList, setFileList] = useState([]);
  // Single mutually-exclusive destination: "pricer" or "audit_trail".
  const [destination, setDestination] = useState(null);
  const [date, setDate] = useState(() => calendarDayjs());
  const [isLoading, setIsLoading] = useState(false);
  // Set when the backend returns 422 with vol_calculation_failed=true. Holds the
  // message, failing option numbers and destination so we can render the popup
  // (with a retry button only when destination === "pricer").
  const [volFailure, setVolFailure] = useState(null);

  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);
  const {
    state: { dateSelected },
  } = useContext(PricerContext);
  const { modalRender, title: draggableTitle } = useDraggableModal(
    isOpen,
    "CSV Import with Pricer/Audit Trail Mode Selection"
  );

  // Audit-trail action date opens on the Pricer working day (UTC).
  const initialDate = useMemo(
    () => calendarDayjs(dateSelected),
    [dateSelected]
  );

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setFileList([]);
      setDestination(null);
      setDate(initialDate);
      setVolFailure(null);
    }
  }, [isOpen, initialDate]);

  const handleFileChange = (info) => {
    const { file } = info;
    
    // Validate file type - only accept CSV files
    const isValidFileType = 
      file.type === "text/csv" || 
      file.name.endsWith(".csv");

    if (!isValidFileType) {
      toast("Please upload a CSV file", "error");
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast(`File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`, "error");
      return;
    }

    // Validate row count for CSV files
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const rows = text.split("\n").filter((row) => row.trim() !== "");
      // Subtract 1 for header row
      const dataRows = rows.length - 1;

      if (dataRows > MAX_ROWS) {
        toast(`CSV file must have maximum ${MAX_ROWS} data rows (excluding header)`, "error");
        return;
      }

      setFileList([file]);
    };
    reader.readAsText(file);
  };

  const beforeUpload = (file) => {
    // Prevent auto upload
    return false;
  };

  const onRemove = () => {
    setFileList([]);
  };

  // Renders import error toasts from the shared rows_with_errors contract.
  // Vol-calculation-failure (handled by runImport) shows a popup instead.
  const showImportErrorToast = (response) => {
    toastImportValidationResponse(response);
  };

  const runImport = async ({ skipVolCalculation = false } = {}) => {
    // Validate file is selected
    if (fileList.length === 0) {
      toast("Please select a CSV file", "error");
      return;
    }

    // Validate a destination is selected
    if (!destination) {
      toast("Please select an import destination", "error");
      return;
    }

    const isPricer = destination === PRICER_DESTINATION;
    const isAuditTrail = destination === AUDIT_TRAIL_DESTINATION;

    // Validate date if pushing to audit trail
    if (isAuditTrail && !date) {
      toast("Please select date when pushing to audit trail", "error");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      // Re-send the same File object (the server doesn't persist it between
      // attempts), so keep it in state until the import fully succeeds.
      formData.append("file", fileList[0]);
      // Laravel expects boolean values - send as 1/0 (FormData will convert to strings "1"/"0")
      formData.append("import_to_pricer", isPricer ? 1 : 0);
      formData.append("import_to_audit_trail", isAuditTrail ? 1 : 0);

      if (isAuditTrail) {
        // Audit trail prices at the trade/action date. calculation_date is
        // intentionally omitted so a historical trade prices as of date_time.
        formData.append("date_time", date.format("YYYY-MM-DD"));
      } else if (isPricer) {
        // Pricer prices at the current working/calculation date.
        formData.append(
          "calculation_date",
          pricerWorkingDate(dateSelected)
        );
      }

      if (skipVolCalculation) {
        formData.append("skip_vol_calculation", 1);
      }

      const response = await importCSVFile(formData);

      if (isErrorResponse(response)) {
        const data = response?.data?.data;
        // Branch on the flag, not the 422 status alone — plain validation
        // errors share the same status. Don't re-open the popup on the retry.
        if (
          response?.status === 422 &&
          data?.vol_calculation_failed &&
          !skipVolCalculation
        ) {
          setVolFailure({
            message: response.data.message || "",
            failedOptions: Array.isArray(data.failed_options)
              ? data.failed_options
              : [],
            detail: Array.isArray(data.failed_options_detail)
              ? data.failed_options_detail
              : [],
            destination: data.destination,
          });
          return;
        }
        setVolFailure(null);
        showImportErrorToast(response);
        return;
      }

      setVolFailure(null);
      toast(response?.data?.message || "File imported successfully", "success");
      sendEvent({
        category: CATEGORIES.PRICER,
        context: CONTEXTS.PRICER,
        action: ACTIONS.CLICK,
        component: COMPONENT_NAMES.BUTTON,
        eventName: EVENT_NAMES.PRICER_CSV_IMPORT_SUCCESS,
        extraParams: {
          import_to_pricer: isPricer,
          import_to_audit_trail: isAuditTrail,
          skip_vol_calculation: !!skipVolCalculation,
        },
      });

      // Refetch pricer data if imported to pricer (zeroed options need manual fix)
      if (isPricer && refetchOptionRow) {
        refetchOptionRow();
      }

      setFileList([]);
      onClose(false);
    } catch (error) {
      // The interceptor resolves import-csv errors, so this only guards against
      // unexpected rejections (e.g. request setup failures).
      setVolFailure(null);
      toast("Import failed. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = () => runImport();

  const handleRetryWithZeroValues = () =>
    runImport({ skipVolCalculation: true });

  const dismissVolFailure = () => setVolFailure(null);

  const handleCancel = () => {
    onClose(false);
  };

  const isAuditTrailSelected = destination === AUDIT_TRAIL_DESTINATION;

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    const hasFile = fileList.length > 0;
    const hasDestination = !!destination;
    const hasDate = !isAuditTrailSelected || !!date;

    return hasFile && hasDestination && hasDate;
  }, [fileList.length, destination, isAuditTrailSelected, date]);

  const theme = dark_Theme
    ? "other-date-picker-dark"
    : "other-date-picker-light";

  const canRetryVolFailure = volFailure?.destination === PRICER_DESTINATION;

  return (
    <>
    <Modal
      getContainer={"#content-root"}
      title={draggableTitle}
      modalRender={modalRender}
      width={"50vw"}
      onCancel={handleCancel}
      open={isOpen}
      footer={[
        <CancelButton key="cancel" onClick={handleCancel}>
          Cancel
        </CancelButton>,
        <PrimaryButton
          key="import"
          loading={isLoading}
          onClick={handleImport}
          disabled={!isFormValid || isLoading}
        >
          Import
        </PrimaryButton>,
      ]}
      className={`csv-import__container ${
        dark_Theme ? "ant-modal-dark" : "ant-modal-light"
      }`}
    >
      <div className="csv-import__content">
        <div className="csv-import__section">
          <label className="csv-import__label">
            Upload CSV File (Max 50 rows)
          </label>
          <div className="csv-import__upload-container">
            <Upload.Dragger
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleFileChange}
              onRemove={onRemove}
              accept=".csv"
              maxCount={1}
              showUploadList={false}
              className={`csv-import__dragger ${dark_Theme ? "csv-import__dragger--dark" : ""}`}
            >
              <p className={`csv-import__dragger-icon ${dark_Theme ? "csv-import__dragger-icon--dark" : ""}`}>
                <UploadOutlined />
              </p>
              <p className={`csv-import__dragger-text ${dark_Theme ? "csv-import__dragger-text--dark" : ""}`}>
                Click or drag CSV file here
              </p>
            </Upload.Dragger>
            {fileList.length > 0 && (
              <span className="csv-import__file-info">
                {fileList[0].name} ({(fileList[0].size / 1024).toFixed(2)} KB)
              </span>
            )}
          </div>
        </div>

        <div className="csv-import__section">
          <label className="csv-import__label">
            Import Destination:
          </label>
          <Radio.Group
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
          >
            <Space direction="vertical">
              <Radio value={PRICER_DESTINATION}>Import to Pricer</Radio>
              <Radio value={AUDIT_TRAIL_DESTINATION}>Push to Audit Trail</Radio>
            </Space>
          </Radio.Group>
        </div>

        {isAuditTrailSelected && (
          <div className="csv-import__section">
            <label className="csv-import__label">
              Date (Required when pushing to audit trail) <span className="csv-import__required-mark">*</span>
            </label>
            <DatePicker
              value={date}
              className={theme}
              popupClassName={dark_Theme ? "date-picker-dropdown-dark" : ""}
              onChange={(value) => setDate(value)}
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
            />
          </div>
        )}
      </div>
    </Modal>

      <VolCalcFailedModal
        open={!!volFailure}
        info={volFailure}
        canRetry={canRetryVolFailure}
        loading={isLoading}
        onRetry={handleRetryWithZeroValues}
        onCancel={dismissVolFailure}
        title="Some options couldn't be priced"
        retryLabel="Import with 0 values"
        showOptionList={false}
        description={
          canRetryVolFailure ? null : (
            <p style={{ margin: 0 }}>
              These must be corrected before pushing to the Audit Trail — every
              option must calculate and have no expired tenors. Adjust the
              premium, strike, or underlying, or send the correct action date for
              the push, then re-upload.
            </p>
          )
        }
      />
    </>
  );
};

export default CSVImportModal;
